// Accessibility utilities

// Check if user prefers reduced motion
export const prefersReducedMotion = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// Check if user prefers high contrast
export const prefersHighContrast = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-contrast: high)').matches;
};

// Initialize focus-visible polyfill if needed
export const initFocusVisible = () => {
  if (typeof window === 'undefined') return;
  
  // Add focus-visible class to body if polyfill is needed
  if (!CSS.supports('selector(:focus-visible)')) {
    document.body.classList.add('js-focus-visible');
    
    // Simple polyfill: add focus-visible class on keyboard navigation
    document.addEventListener('keydown', () => {
      document.body.classList.add('keyboard-navigation');
    });
    
    document.addEventListener('mousedown', () => {
      document.body.classList.remove('keyboard-navigation');
    });
  }
};

// Announce to screen readers
export const announceToScreenReader = (message, priority = 'polite') => {
  const announcement = document.createElement('div');
  announcement.setAttribute('role', 'status');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only';
  announcement.textContent = message;
  
  document.body.appendChild(announcement);
  
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};

// Check color contrast ratio (WCAG)
export const getContrastRatio = (color1, color2) => {
  // Simplified contrast calculation
  // For production, use a proper color contrast library
  const getLuminance = (hex) => {
    const rgb = hex.match(/[A-Za-z0-9]{2}/g).map((v) => parseInt(v, 16));
    const [r, g, b] = rgb.map((val) => {
      val = val / 255;
      return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };
  
  const lum1 = getLuminance(color1);
  const lum2 = getLuminance(color2);
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  
  return (lighter + 0.05) / (darker + 0.05);
};

// Ensure minimum contrast ratio (WCAG AA = 4.5:1, AAA = 7:1)
export const meetsContrastRatio = (color1, color2, level = 'AA') => {
  const ratio = getContrastRatio(color1, color2);
  return level === 'AA' ? ratio >= 4.5 : ratio >= 7;
};
